import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { AlertTriangle, Info, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["pre_a1","a1","a1_plus","a2","a2_plus","b1","b1_plus","b2","b2_plus","c1","c1_plus","c2"];
const WD = [["Mon",0],["Tue",1],["Wed",2],["Thu",3],["Fri",4],["Sat",5],["Sun",6]];

function emptyForm() {
  return {
    name: "", level: "a1", description: "",
    teacher_user_id: "", secondary_teacher_user_id: "",
    room_id: "", capacity: 10,
    schedule: [{ weekday: 0, start_time: "17:00", end_time: "18:00" }],
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 84 * 86400000).toISOString().slice(0, 10),
    enrolled_student_ids: [], status: "active", notes: "",
  };
}

export function ClassEditModal({ open, initial, rooms, teachers, students, onClose, onSaved, isAdmin }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm());
  const [conflicts, setConflicts] = useState([]);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [studentQ, setStudentQ] = useState("");

  useEffect(() => {
    if (initial) {
      setForm({
        ...emptyForm(), ...initial,
        secondary_teacher_user_id: initial.secondary_teacher_user_id || "",
      });
    } else {
      setForm(emptyForm());
    }
    setConflicts([]); setOverride(false); setReason("");
  }, [initial, open]);

  // debounced conflict preview
  useEffect(() => {
    const handle = setTimeout(async () => {
      if (!form.teacher_user_id || !form.room_id || !form.schedule.length) return;
      try {
        const params = initial?.id
          ? `?exclude_class_id=${encodeURIComponent(initial.id)}`
          : "";
        const { data } = await http.post(`/classes/preview-conflicts${params}`, form);
        setConflicts(data.conflicts || []);
      } catch (e) { /* ignore preview errors */ }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.teacher_user_id, form.secondary_teacher_user_id, form.room_id,
      JSON.stringify(form.schedule), form.start_date, form.end_date, initial?.id]); // eslint-disable-line

  const room = rooms.find(r => r.id === form.room_id);
  const blocking = conflicts.filter(c => c.type !== "on_holiday");
  const warnings = conflicts.filter(c => c.type === "on_holiday");
  const optionsLoaded = teachers.length > 0 && rooms.length > 0;
  const canSave = optionsLoaded && (blocking.length === 0 || (isAdmin && override && reason.trim()));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateBlock = (i, key, val) => setForm(f => {
    const sched = [...f.schedule]; sched[i] = { ...sched[i], [key]: val };
    return { ...f, schedule: sched };
  });
  const addBlock = () => setForm(f => ({ ...f, schedule: [...f.schedule, { weekday: 0, start_time: "17:00", end_time: "18:00" }] }));
  const removeBlock = (i) => setForm(f => ({ ...f, schedule: f.schedule.filter((_, idx) => idx !== i) }));

  const toggleStudent = (sid) => setForm(f => {
    const has = (f.enrolled_student_ids || []).includes(sid);
    const roster = has ? f.enrolled_student_ids.filter(x => x !== sid)
                       : [...(f.enrolled_student_ids || []), sid];
    return { ...f, enrolled_student_ids: roster };
  });

  const filteredStudents = useMemo(() => {
    if (!studentQ) return students;
    const q = studentQ.toLowerCase();
    return students.filter(s => `${s.first_name} ${s.last_name}`.toLowerCase().includes(q));
  }, [students, studentQ]);

  const submit = async () => {
    if (room && form.capacity > room.capacity) {
      toast.error(`Capacity ${form.capacity} exceeds room capacity ${room.capacity}`);
      return;
    }
    setSaving(true);
    try {
      const params = override && isAdmin ? "?override=true" : "";
      const body = { ...form, override: override && isAdmin, override_reason: reason || undefined };
      if (initial) {
        await http.patch(`/classes/${initial.id}${params}`, body);
      } else {
        await http.post(`/classes${params}`, body);
      }
      toast.success(t("classes.saved"));
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.conflicts) {
        setConflicts(detail.conflicts);
        toast.error(t("classes.conflictsFound"));
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
            {initial ? t("classes.edit") : t("classes.createTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {/* Basic */}
          <section className="grid gap-3">
            <h3 className="font-medium text-sm uppercase tracking-wider text-slate-500">{t("classes.section.basic")}</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>{t("classes.name")} *</Label>
                <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="class-form-name" />
              </div>
              <div>
                <Label>{t("classes.level")} *</Label>
                <Select value={form.level} onValueChange={v => set("level", v)}>
                  <SelectTrigger data-testid="class-form-level"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map(l => <SelectItem key={l} value={l}>{l.replace("_"," ").toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("classes.description")}</Label>
              <Textarea value={form.description || ""} onChange={e => set("description", e.target.value)} />
            </div>
          </section>

          {/* Teacher + Room */}
          <section className="grid gap-3">
            <h3 className="font-medium text-sm uppercase tracking-wider text-slate-500">{t("classes.section.teacherRoom")}</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>{t("classes.teacher")} *</Label>
                <Select value={form.teacher_user_id} onValueChange={v => set("teacher_user_id", v)}>
                  <SelectTrigger data-testid="class-form-teacher"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    {teachers.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("classes.room")} *</Label>
                <Select value={form.room_id} onValueChange={v => set("room_id", v)}>
                  <SelectTrigger data-testid="class-form-room"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.capacity})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("classes.capacity")} *</Label>
                <Input type="number" min={1} max={room?.capacity || 200} value={form.capacity}
                       onChange={e => set("capacity", Number(e.target.value))}/>
              </div>
            </div>
          </section>

          {/* Schedule */}
          <section className="grid gap-3">
            <h3 className="font-medium text-sm uppercase tracking-wider text-slate-500">{t("classes.section.schedule")}</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div><Label>{t("classes.startDate")}</Label>
                <Input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)}/></div>
              <div><Label>{t("classes.endDate")}</Label>
                <Input type="date" value={form.end_date} onChange={e => set("end_date", e.target.value)}/></div>
            </div>
            <div className="space-y-2">
              {form.schedule.map((b, i) => (
                <div key={i} className="grid gap-2 md:grid-cols-[1fr_120px_120px_auto] items-end p-3 bg-slate-50 rounded-lg">
                  <div><Label>{t("classes.weekday")}</Label>
                    <Select value={String(b.weekday)} onValueChange={v => updateBlock(i, "weekday", Number(v))}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>{WD.map(([n, k]) => <SelectItem key={k} value={String(k)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("classes.start_time")}</Label>
                    <Input type="time" value={b.start_time} onChange={e => updateBlock(i, "start_time", e.target.value)}/></div>
                  <div><Label>{t("classes.end_time")}</Label>
                    <Input type="time" value={b.end_time} onChange={e => updateBlock(i, "end_time", e.target.value)}/></div>
                  <Button variant="ghost" size="icon" onClick={() => removeBlock(i)} disabled={form.schedule.length <= 1}>
                    <Trash2 className="h-4 w-4 text-[#C8102E]"/>
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addBlock} data-testid="class-add-block">
                <Plus className="h-3.5 w-3.5 mr-1"/>{t("classes.addBlock")}
              </Button>
            </div>
          </section>

          {/* Conflict cards */}
          {(blocking.length > 0 || warnings.length > 0) && (
            <section className="space-y-2" data-testid="class-conflicts">
              {blocking.map((c, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg border border-[#C8102E]/40 bg-[#C8102E]/5">
                  <AlertTriangle className="h-5 w-5 text-[#C8102E] shrink-0"/>
                  <div className="text-sm">
                    <p className="font-medium text-[#C8102E]">{c.type.replaceAll("_"," ")}</p>
                    <p className="text-slate-700">{c.message}</p>
                  </div>
                </div>
              ))}
              {warnings.map((c, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50">
                  <Info className="h-5 w-5 text-amber-600 shrink-0"/>
                  <div className="text-sm">
                    <p className="font-medium text-amber-700">{c.type.replaceAll("_"," ")}</p>
                    <p className="text-slate-700">{c.message}</p>
                  </div>
                </div>
              ))}
            </section>
          )}

          {blocking.length > 0 && isAdmin && (
            <section className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2" data-testid="class-override-section">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={override} onCheckedChange={setOverride} data-testid="class-override-checkbox"/>
                <span className="text-sm font-medium">{t("classes.override")} ({t("classes.requiresReason")})</span>
              </label>
              {override && (
                <Textarea placeholder={t("classes.overridePlaceholder")} value={reason}
                          onChange={e => setReason(e.target.value)} data-testid="class-override-reason"/>
              )}
            </section>
          )}

          {/* Roster */}
          <section className="grid gap-2">
            <h3 className="font-medium text-sm uppercase tracking-wider text-slate-500">{t("classes.section.roster")} ({(form.enrolled_student_ids || []).length}/{form.capacity})</h3>
            <Input placeholder={t("classes.searchStudent")} value={studentQ} onChange={e => setStudentQ(e.target.value)}/>
            <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 bg-white">
              {filteredStudents.map(s => {
                const checked = (form.enrolled_student_ids || []).includes(s.id);
                return (
                  <label key={s.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={checked} onCheckedChange={() => toggleStudent(s.id)}/>
                    <span className="text-sm">{s.first_name} {s.last_name}</span>
                    <Badge variant="secondary" className="ml-auto text-[10px]">{s.level}</Badge>
                  </label>
                );
              })}
            </div>
          </section>

          {/* Notes + Status */}
          <section className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>{t("classes.status")}</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("common.active")}</SelectItem>
                  <SelectItem value="draft">{t("classes.status_draft")}</SelectItem>
                  <SelectItem value="archived">{t("classes.status_archived")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:row-span-2"><Label>{t("classes.notes")}</Label>
              <Textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)}/></div>
          </section>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={!canSave || saving} className="bg-[#0A1F44] hover:bg-[#0A1F44]/90"
                  data-testid="class-form-save">
            {override && isAdmin ? t("classes.saveAnyway") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
